import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../services/categoryService';
import { uploadFile } from '../../services/upload';
import './Categories.css';

const PRODUCT_STATUS = {
  DELETED: 0,
  INACTIVE: 100,
  ACTIVE: 200,
};

const getStatusMeta = (value) => {
  const num = Number(value);
  if (num === PRODUCT_STATUS.ACTIVE) return { label: 'Active', tone: 'success' };
  if (num === PRODUCT_STATUS.INACTIVE) return { label: 'Inactive', tone: 'warning' };
  if (num === PRODUCT_STATUS.DELETED) return { label: 'Deleted', tone: 'muted' };
  return { label: 'Unknown', tone: 'muted' };
};

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('uz');
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [total, setTotal] = useState(0);

  const iconInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: { uz: '', ru: '', oz: '' },
    description: { uz: '', ru: '', oz: '' },
    icon: '',
  });

  const fetchCategories = useCallback(async () => {
    try {
      const offset = (page - 1) * limit;
      const response = await getCategories({
        limit,
        offset,
        search
      });
      setCategories(response.data.categories || []);
      setTotal(response.data.count || 0);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [page, limit, search]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleInputChange = (field, lang, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: {
        ...prev[field],
        [lang]: value
      }
    }));
  };

  const handleIconFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadFile(file);
      const link = response?.data?.link || '';
      setFormData((prev) => ({ ...prev, icon: link }));
    } catch (error) {
      console.error('Error uploading category icon:', error);
    } finally {
      setUploading(false);
      if (iconInputRef.current) iconInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (currentCategory) {
        await updateCategory({
          ...formData,
          guid: currentCategory.guid,
          status: 200 // Active status
        });
      } else {
        await createCategory(formData);
      }
      setModalOpen(false);
      fetchCategories();
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };

  const openModal = (category = null) => {
    if (category) {
      setCurrentCategory(category);
      setFormData({
        name: category.name,
        description: category.description,
        icon: category.icon || '',
      });
    } else {
      setCurrentCategory(null);
      setFormData({
        name: { uz: '', ru: '', oz: '' },
        description: { uz: '', ru: '', oz: '' },
        icon: '',
      });
    }
    setModalOpen(true);
  };

  const confirmDelete = (category) => {
    setCategoryToDelete(category);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (categoryToDelete) {
      try {
        await deleteCategory(categoryToDelete.guid);
        fetchCategories();
        setDeleteModalOpen(false);
        setCategoryToDelete(null);
      } catch (error) {
        console.error('Error deleting category:', error);
      }
    }
  };

  return (
    <div className="categories-page">
      <div className="page-header">
        <div className="search-box">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            type="text"
            placeholder="Search categories..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div className="header-actions">
          <button className="add-btn" onClick={() => openModal()}>
            + Add Category
          </button>
        </div>
      </div>

      <div className="categories-table-container">
        <table className="categories-table">
          <thead>
            <tr>
              <th>№</th>
              <th>Name (UZ)</th>
              <th>Description (UZ)</th>
              <th>Icon</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {categories.map((category, index) => {
              const statusMeta = getStatusMeta(category.status);
              return (
              <tr key={category.guid}>
                <td>{(page - 1) * limit + index + 1}</td>
                <td>{category.name.uz}</td>
                <td>{category.description.uz}</td>
                <td>
                  {category.icon ? (
                    <img src={category.icon} alt={category.name?.uz || 'icon'} className="category-icon-preview" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 4 }} />
                  ) : (
                    '-'
                  )}
                </td>
                <td>
                  <span className={`status-badge ${statusMeta.tone}`}>{statusMeta.label}</span>
                </td>
                <td>
                  <button className="action-btn" onClick={() => openModal(category)} aria-label="Edit category">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button className="action-btn delete" onClick={() => confirmDelete(category)} aria-label="Delete category">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="pagination-info">
          Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total} entries
        </div>
        <div className="pagination-controls">
          <button 
            disabled={page === 1} 
            onClick={() => handlePageChange(page - 1)}
            className="page-btn"
          >
            Previous
          </button>
          {[...Array(Math.ceil(total / limit))].map((_, i) => (
            <button
              key={i + 1}
              className={`page-btn ${page === i + 1 ? 'active' : ''}`}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
          <button 
            disabled={page >= Math.ceil(total / limit)} 
            onClick={() => handlePageChange(page + 1)}
            className="page-btn"
          >
            Next
          </button>
        </div>
      </div>

      {deleteModalOpen && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '400px' }}>
            <div className="modal-header">
              <h2>Delete Category</h2>
              <button className="close-btn" onClick={() => setDeleteModalOpen(false)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ color: 'var(--text-secondary)' }}>
                Are you sure you want to delete the category <strong>{categoryToDelete?.name?.uz}</strong>? This action cannot be undone.
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteModalOpen(false)}>Cancel</button>
              <button className="btn-save" style={{ backgroundColor: 'var(--error)' }} onClick={handleDelete}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {modalOpen && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{currentCategory ? 'Edit Category' : 'Add Category'}</h2>
              <button className="close-btn" onClick={() => setModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Icon</label>
                  <label className="file-upload">
                    {formData.icon ? (
                      <img src={formData.icon} alt="Icon preview" className="preview-image" />
                    ) : (
                      <p>Click to upload icon</p>
                    )}
                    <input
                      ref={iconInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleIconFileChange}
                      style={{ display: 'none' }}
                    />
                  </label>
                  {uploading && <p>Uploading...</p>}
                </div>

                <div className="lang-tabs">
                  {['uz', 'ru', 'oz'].map(lang => (
                    <button
                      key={lang}
                      type="button"
                      className={`lang-tab ${activeTab === lang ? 'active' : ''}`}
                      onClick={() => setActiveTab(lang)}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="form-group">
                  <label>Name ({activeTab.toUpperCase()})</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name[activeTab]}
                    onChange={(e) => handleInputChange('name', activeTab, e.target.value)}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Description ({activeTab.toUpperCase()})</label>
                  <textarea
                    className="form-control"
                    rows="3"
                    value={formData.description[activeTab]}
                    onChange={(e) => handleInputChange('description', activeTab, e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={() => setModalOpen(false)}>Cancel</button>
                <button type="submit" className="btn-save">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;
